import React from 'react';
import { Card, Tooltip, Tag, Typography, Alert, Button } from 'antd';
import {
    HolderOutlined,
    LinkOutlined,
    UploadOutlined,
    UserOutlined,
    ClockCircleOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { AttractorImageDto } from '../../types/attractortypes';
import { useAuth } from '../../auth/authcontext';
import ImageWithSpinner from '../content/imagewithspinner';

const { Text, Paragraph } = Typography;

export interface SortableSlideItemProps {
    id: string;
    slide: AttractorImageDto;
    index: number;
    getUserName: (guid: string) => string;
    handleDelete: (index: number) => void;
    warningText?: string;
}

const SortableSlideItem: React.FC<SortableSlideItemProps> = ({
    id,
    slide,
    index,
    getUserName,
    handleDelete,
    warningText
}) => {
    const { isReadOnly } = useAuth();
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        borderRadius: '12px',
        border: isDragging ? '1px solid var(--color-primary, #1e1e38)' : '1px solid #e2e8f0',
        boxShadow: isDragging
            ? '0 10px 15px -3px rgba(30, 30, 56, 0.2), 0 4px 6px -2px rgba(30, 30, 56, 0.1)'
            : '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
        background: isDragging ? '#f1f5f9' : 'white',
        opacity: isDragging ? 0.85 : 1,
        zIndex: isDragging ? 999 : 'auto',
        position: 'relative',
        marginBottom: '12px'
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            <Card
                hoverable
                bodyStyle={{ padding: '16px' }}
                style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
            >
                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                    {/* Drag Handle */}
                    <Tooltip title="Drag to reorder">
                        <div
                            {...listeners}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: isDragging ? 'grabbing' : 'grab',
                                padding: '8px 4px',
                                color: '#94a3b8',
                                transition: 'color 0.2s'
                            }}
                        >
                            <HolderOutlined style={{ fontSize: '20px' }} />
                        </div>
                    </Tooltip>

                    {/* Image Preview */}
                    <div
                        style={{
                            width: '120px',
                            height: '120px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            background: '#f1f5f9',
                            border: '1px solid #e2e8f0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        {slide.imageUrl ? (
                            <ImageWithSpinner imageUrl={slide.imageUrl} />
                        ) : slide.imageBase64 ? (
                            <img
                                src={`data:image/png;base64,${slide.imageBase64}`}
                                alt={`Uploaded Slide ${index + 1}`}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                        ) : (
                            <Text type="secondary">Broken Image</Text>
                        )}
                    </div>

                    {/* Slide Info & Metadata */}
                    <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <Tag color="blue" style={{ fontWeight: 600 }}>
                                Position #{index + 1}
                            </Tag>
                            {slide.imageUrl ? (
                                <Tag icon={<LinkOutlined />} color="purple">
                                    External URL
                                </Tag>
                            ) : (
                                <Tag icon={<UploadOutlined />} color="orange">
                                    Local File (Base64)
                                </Tag>
                            )}
                        </div>

                        <div style={{ margin: '4px 0' }}>
                            {slide.imageUrl ? (
                                <Paragraph
                                    ellipsis={{ rows: 2, tooltip: true }}
                                    style={{ fontSize: '13px', color: '#475569', margin: 0, fontFamily: 'monospace' }}
                                >
                                    {slide.imageUrl}
                                </Paragraph>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                    <Text style={{ fontSize: '13px', color: '#475569', fontWeight: 500, fontFamily: 'monospace' }}>
                                        {slide.fileName || 'Unnamed File'}
                                    </Text>
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        {slide.contentType || 'image/*'} • Raw data ({Math.round((((slide.imageBase64?.length ?? 0) * 3) / 4) / 1024)} KB)
                                    </Text>

                                </div>
                            )}
                        </div>

                        {warningText && (
                            <Alert
                                message={warningText}
                                type="warning"
                                showIcon
                                style={{ padding: '4px 8px', fontSize: '12px' }}
                            />
                        )}

                        <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#64748b' }}>
                            <span>
                                <UserOutlined style={{ marginRight: '4px' }} />
                                Uploaded by: <Text strong>{getUserName(slide.createdBy)}</Text>
                            </span>
                            {slide.dateCreated && (
                                <span>
                                    <ClockCircleOutlined style={{ marginRight: '4px' }} />
                                    {new Date(slide.dateCreated).toLocaleDateString()}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Action Controls */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {!isReadOnly && (
                            <Tooltip title="Remove Slide">
                                <Button
                                    type="primary"
                                    danger
                                    ghost
                                    shape="circle"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDelete(index)}
                                />
                            </Tooltip>
                        )}
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default SortableSlideItem;
